import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from "../../stores";
import { FamlaServices } from '../../services';
import { iAlert } from '../../interfaces';
import { InputForm, Spiner } from '../../components';
import { Check, Plus, X } from 'lucide-react';
import { isPrivateEmail } from '../../utils/emailUtils';
import {
    dedupeRoleOptions,
    dedupeRoleOptionsPreservingLabels,
    formatInviteRoleLabel,
    normalizeInviteRoleKey,
} from '../../utils/inviteRoleOptions';
import CopyResponderLinkButton from '../../components/CopyResponderLinkButton';

export type InviteRoleOption = Readonly<{ role?: string | null }>;

export type InviteCustomSubmitResult =
    | { ok: true }
    | { ok: false; message: string };

interface ChildProps {
    processId: string;
    closeRef: any;
    interviewwId: string | undefined;
    invited: string | undefined;
    /** Rôles proposés dans le select (process interview: interviews ; data collection: target_roles). */
    availableRoles?: ReadonlyArray<InviteRoleOption>;
    /** Collect Data — lien répondeur public partagé. */
    responderLinkToken?: string | null;
    /** Collect Data — select de rôle uniquement (pas de saisie libre « Enter a role »). */
    roleSelectOnly?: boolean;
    onCustomSubmit?: (data: { emails: string[]; names: string[]; role: string }) => Promise<boolean | InviteCustomSubmitResult>;
    /** Called after a successful process invite (avoids full page reload when provided). */
    onInviteSuccess?: () => void | Promise<void>;
}

const InviteUser = ({ processId, closeRef, interviewwId, invited, availableRoles, responderLinkToken, roleSelectOnly, onCustomSubmit, onInviteSuccess }: ChildProps) => {
    const { t } = useTranslation();
    const users = useAppSelector(state => state.auth);
    const [emails, setEmails] = useState<string[]>(['']);
    const [whatsappPhones, setWhatsappPhones] = useState<string[]>(['']);
    const [whatsappConsent, setWhatsappConsent] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [alert, setAlert] = useState<iAlert>({ isActive: false, type: '', message: '' });
    const [role, setRole] = useState(() => formatInviteRoleLabel(invited || '') || invited || '');
    const closeTimerRef = useRef<number | null>(null);
    const usesDataCollectionRoleSemantics = Boolean(roleSelectOnly || onCustomSubmit);
    const roleOptions = useMemo(() => {
        const source = [...(availableRoles ?? []), invited];
        return usesDataCollectionRoleSemantics
            ? dedupeRoleOptionsPreservingLabels(source)
            : dedupeRoleOptions(source);
    }, [availableRoles, invited, usesDataCollectionRoleSemantics]);

    // Aligne la valeur du select sur une option normalisée (évite "client" vs "Client")
    const selectValue = useMemo(() => {
        const key = normalizeInviteRoleKey(role || invited || '');
        if (!key) return '';
        const match = roleOptions.find((o) => normalizeInviteRoleKey(o.role) === key);
        return match?.role ?? '';
    }, [role, invited, roleOptions]);

    // Même libellé que le select (Title Case, sans article) pour le champ « Entrer un rôle »
    const displayRole = selectValue || role;

    const syncRoleFromInvited = useCallback(
        (raw?: string | null) => {
            const trimmed = String(raw || '').trim();
            if (!trimmed) {
                setRole('');
                return;
            }
            const key = normalizeInviteRoleKey(trimmed);
            const match = roleOptions.find((o) => normalizeInviteRoleKey(o.role) === key);
            setRole(match?.role || formatInviteRoleLabel(trimmed) || trimmed);
        },
        [roleOptions]
    );

    useEffect(() => {
        if (!invited) return;
        // Toujours suivre le rôle de la question d'invitation courante
        // (sinon un « Non » sur Client laisse Client quand on dit « Oui » au réceptionniste)
        const key = normalizeInviteRoleKey(invited);
        const match = roleOptions.find((o) => normalizeInviteRoleKey(o.role) === key);
        setRole(match?.role || formatInviteRoleLabel(invited) || invited);
    }, [invited, roleOptions]);

    const resetInviteForm = useCallback(() => {
        if (closeTimerRef.current != null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setEmails(['']);
        setWhatsappPhones(['']);
        setWhatsappConsent(false);
        syncRoleFromInvited(invited);
        setAlert({ isActive: false, type: '', message: '' });
        setIsSubmit(false);
    }, [invited, syncRoleFromInvited]);

    useEffect(() => {
        if (invited) setRole(invited);
    }, [invited]);

    useEffect(() => {
        const el = document.getElementById('inviteUser');
        if (!el) return;
        const onHidden = () => {
            resetInviteForm();
        };
        el.addEventListener('hidden.bs.modal', onHidden);
        return () => {
            el.removeEventListener('hidden.bs.modal', onHidden);
            if (closeTimerRef.current != null) {
                window.clearTimeout(closeTimerRef.current);
            }
        };
    }, [resetInviteForm]);

    const addEmailField = () => {
        setEmails([...emails, '']);
    };

    const removeEmailField = (index: number) => {
        setEmails(emails.filter((_, i) => i !== index));
    };

    const readInputValue = (event: React.SyntheticEvent) =>
        (event.currentTarget as HTMLInputElement).value;

    const handleEmailChange = (index: number, event: React.SyntheticEvent) => {
        const newEmails = [...emails];
        newEmails[index] = readInputValue(event);
        setEmails(newEmails);
    };

    const handlePhoneChange = (index: number, event: React.SyntheticEvent) => {
        const next = [...whatsappPhones];
        next[index] = readInputValue(event);
        setWhatsappPhones(next);
    };

    const addPhoneField = () => setWhatsappPhones([...whatsappPhones, '']);
    const removePhoneField = (index: number) => setWhatsappPhones(whatsappPhones.filter((_, i) => i !== index));

    const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRole(event.target.value);  
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setAlert({ isActive: false, type: '', message: '' });
        setIsSubmit(true);

        const rowCount = Math.max(emails.length, whatsappPhones.length, 1);
        const mailRows = Array.from({ length: rowCount }, (_, i) => (emails[i] ?? '').trim());
        const phoneRows = Array.from({ length: rowCount }, (_, i) => (whatsappPhones[i] ?? '').trim());
        const validEmails = mailRows.filter(Boolean);
        const validPhones = phoneRows.filter(Boolean);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = validEmails.filter(email => !emailRegex.test(email));

        if (invalidEmails.length > 0) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.enterValidEmails') });
            setIsSubmit(false);
            return;
        }

        const privateEmails = validEmails.filter(email => isPrivateEmail(email));
        if (privateEmails.length > 0) {
            setAlert({ isActive: true, type: 'warning', message: t('views.auth.professionalEmail') });
            setIsSubmit(false);
            return;
        }

        if (validEmails.length === 0 && validPhones.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.enterEmailOrPhone') });
            setIsSubmit(false);
            return;
        }

        if (validPhones.length > 0 && !whatsappConsent) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.whatsappConsentRequired') });
            setIsSubmit(false);
            return;
        }

        const rawRole = String(selectValue || role || invited || '').trim();
        // Data Collection conserve le libellé configuré ; Process Interview utilise le format canonique.
        const resolvedRole = (
            usesDataCollectionRoleSemantics
                ? rawRole
                : formatInviteRoleLabel(rawRole) || rawRole
        ).trim();
        if (!resolvedRole) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.selectRole') });
            setIsSubmit(false);
            return;
        }

        if (onCustomSubmit) {
            const raw = await onCustomSubmit({
                emails: validEmails,
                names: validEmails.map(() => ''),
                role: resolvedRole,
            });
            const success = typeof raw === 'boolean' ? raw : raw.ok;
            const failMessage =
                typeof raw === 'object' && raw !== null && raw.ok === false ? raw.message || '' : '';
            if (success) {
                setAlert({ isActive: true, type: 'success', message: t('navigation.invitationSentSuccessfully') });
                setEmails(['']);
                setRole('');
                if (closeRef?.current) {
                    if (closeTimerRef.current != null) {
                        window.clearTimeout(closeTimerRef.current);
                    }
                    closeTimerRef.current = window.setTimeout(() => {
                        closeRef.current?.click();
                        closeTimerRef.current = null;
                    }, 1400);
                }
            } else {
                setAlert({
                    isActive: true,
                    type: 'danger',
                    message: failMessage.trim() || t('navigation.anErrorOccurred'),
                });
            }
            setIsSubmit(false);
            return;
        }

        const mailPayload = mailRows;
        const phonePayload = phoneRows;
        const hasPhones = phonePayload.some(Boolean);

        let newFormData: Record<string, unknown> = {
            process_id: processId,
            mail: mailPayload.some(Boolean) ? mailPayload : [],
            role: resolvedRole,
            whatsapp_consent: hasPhones ? whatsappConsent : false,
        };
        if (hasPhones) {
            newFormData.whatsapp_phones = phonePayload;
        }

        let data = await FamlaServices("api_host", `/process/add_member/`, "PUT", newFormData, users.access_token);

        if (data.status === 200) {
            setAlert({ isActive: true, type: 'success', message: t('navigation.invitationSentSuccessfully') });
            setEmails(['']);
            setWhatsappPhones(['']);
            setWhatsappConsent(false);
            setRole("");
            if (onInviteSuccess) {
                await onInviteSuccess();
                if (closeRef?.current) {
                    if (closeTimerRef.current != null) {
                        window.clearTimeout(closeTimerRef.current);
                    }
                    closeTimerRef.current = window.setTimeout(() => {
                        closeRef.current?.click();
                        closeTimerRef.current = null;
                    }, 800);
                }
            } else {
                window.location.assign(`/process/interviews/${interviewwId}`);
            }
        } else {
            setAlert({ isActive: true, type: 'danger', message: t("navigation.anErrorOccurred") });
        }
        setIsSubmit(false);
    };

    return (
        <div className="modal" data-bs-backdrop="static" id='inviteUser' tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content bg-dark">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">{t('navigation.inviteCollaborator')}</h5>
                        <button type="button" className="btn-close bg-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body overflow-x-hidden">
                        {alert.isActive && <div className={`alert alert-${alert.type}`} role="alert">{alert.message}</div>}

                        {emails.map((email, index) => (
                            <div key={index} className="d-flex align-items-end mb-3 gap-2">
                                <div className="flex-grow-1">
                                    <InputForm
                                        inputType="email"
                                        inputLabel={t('words.email')}
                                        inputName={`email-${index}`}
                                        inputValue={email}
                                        isFloatingLabel={true}
                                        labelClass="text-theme"
                                        onChange={(e) => handleEmailChange(index, e)}
                                        inputClass="bg-dark text-light"
                                    />
                                </div>
                                {index > 0 && (
                                    <button type="button" className="btn btn-theme btn-sm mb-1" onClick={() => removeEmailField(index)}>
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        ))}

                        {emails.length < 5 &&
                            <button type="button" className="btn btn-none mb-3 d-flex text-primary gap-1" onClick={addEmailField}>
                                <Plus className='mt-1' size={20} /> {t('navigation.addAnotherEmail')}
                            </button>
                        }

                        <p className="text-theme small mb-2">{t('navigation.whatsappOptionalHint')}</p>
                        {whatsappPhones.map((phone, index) => (
                            <div key={`wa-${index}`} className="d-flex align-items-end mb-3 gap-2">
                                <div className="flex-grow-1">
                                    <InputForm
                                        inputType="tel"
                                        inputLabel={t('navigation.whatsappPhone')}
                                        inputName={`whatsapp-${index}`}
                                        inputValue={phone}
                                        isFloatingLabel={true}
                                        labelClass="text-theme"
                                        onChange={(e) => handlePhoneChange(index, e)}
                                        inputClass="bg-dark text-light"
                                    />
                                </div>
                                {index > 0 && (
                                    <button type="button" className="btn btn-theme btn-sm mb-1" onClick={() => removePhoneField(index)}>
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {whatsappPhones.length < 5 && (
                            <button type="button" className="btn btn-none mb-3 d-flex text-primary gap-1" onClick={addPhoneField}>
                                <Plus className='mt-1' size={20} /> {t('navigation.addAnotherPhone')}
                            </button>
                        )}
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={whatsappConsent}
                            className="btn w-100 text-start d-flex align-items-start gap-3 mb-3 p-3 rounded-2 border"
                            style={{
                                borderColor: whatsappConsent ? '#0d6efd' : 'rgba(255,255,255,0.25)',
                                backgroundColor: whatsappConsent ? 'rgba(13, 110, 253, 0.12)' : 'transparent',
                            }}
                            onClick={() => setWhatsappConsent((prev) => !prev)}
                        >
                            <span
                                aria-hidden
                                className="flex-shrink-0 d-inline-flex align-items-center justify-content-center rounded border"
                                style={{
                                    width: 22,
                                    height: 22,
                                    marginTop: 2,
                                    borderColor: whatsappConsent ? '#0d6efd' : '#adb5bd',
                                    backgroundColor: whatsappConsent ? '#0d6efd' : '#212529',
                                }}
                            >
                                {whatsappConsent ? <Check size={14} strokeWidth={3} color="#fff" /> : null}
                            </span>
                            <span
                                className="text-theme small flex-grow-1 text-wrap"
                                style={{ minWidth: 0, lineHeight: 1.4 }}
                            >
                                {t('navigation.whatsappConsentLabel')}
                            </span>
                        </button>

                        {/* Select visible dès qu'il y a des rôles connus (invités + mentionnés owner) */}
                        {!roleSelectOnly && roleOptions.length > 0 ? (
                            <>
                                <div className="mb-3">
                                    <select
                                        className="form-select bg-dark text-theme py-3"
                                        value={selectValue}
                                        onChange={(e) => setRole(e.target.value)}
                                        aria-label={t('navigation.selectRole')}
                                    >
                                        <option className='text-theme' value="">{t('navigation.selectRole')}</option>
                                        {roleOptions.map((option) => (
                                            <option key={normalizeInviteRoleKey(option.role)} value={option.role}>
                                                {option.role}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="d-flex align-items-center my-2 gap-1 ">
                                    <hr className="flex-grow-1 border-secondary" />
                                    <span className="mx-2 text-light">{t('words.or')}</span>
                                    <hr className="flex-grow-1 border-secondary" />
                                </div>
                            </>
                        ) : null}

                        {!invited && roleSelectOnly && roleOptions.length > 0 ? (
                            <div className="mb-3">
                                <select
                                    className="form-select bg-dark text-theme py-3"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    aria-label={t('navigation.selectRole')}
                                >
                                    <option className='text-theme' value="">{t('navigation.selectRole')}</option>
                                    {roleOptions.map((option) => (
                                        <option key={option.role} value={option.role}>
                                            {option.role}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : null}

                        {!roleSelectOnly ? (
                            <InputForm
                                inputType="text"
                                inputLabel={t('navigation.enterRole')}
                                inputName="role"
                                inputValue={displayRole}
                                isFloatingLabel={true}
                                labelClass="text-theme"
                                onChange={handleRoleChange}
                                inputClass="bg-dark text-theme"
                            />
                        ) : null}

                    </div>
                    <div className="modal-footer border-secondary d-flex flex-wrap gap-2 justify-content-between">
                        {onCustomSubmit ? (
                            <CopyResponderLinkButton responderLinkToken={responderLinkToken} />
                        ) : (
                            <span />
                        )}
                        <div className="d-flex gap-2 ms-auto">
                            <button ref={closeRef} type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                {onCustomSubmit ? t('words.cancel') : t('words.close')}
                            </button>
                            <button type="button" onClick={handleSubmit} className="btn btn-primary">
                                {isSubmit ? <Spiner className='spinner-border-sm' /> : <>{t('words.invite')}</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteUser;
